import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  decodeBlobToBuffer,
  playBufferViaPassthrough,
  readRouteModePreference,
  ROUTE_MODE,
} from '../utils/audioRoutePassthrough';
import { logRouteEvent, ROUTE_EVENT } from '../utils/routeDiagnostics';
import {
  decodeBlobOnContext,
  getDirectSinkContext,
  playBufferDirect,
} from '../utils/audioRouteDirect';
import {
  AUDIO_SOURCE_MODE_VIRTUAL_CABLE,
  pickVbCableSinkDevice,
  readAudioSourceMode,
  readSinkExplicit,
  persistSinkExplicit,
  shouldAutoFixSink,
} from '../utils/audioSourceManager';
import { rememberDeviceLabels } from '../utils/audioDeviceLabels';
import { catWarn, catError } from '../utils/catLog';

const AudioSettingsContext = createContext();

export const AudioSettingsProvider = ({ children }) => {
  const [outputDevices, setOutputDevices] = useState([]);
  const [inputDevices, setInputDevices] = useState([]);
  const [selectedSinkId, setSelectedSinkId] = useState(() => localStorage.getItem('CATINTASSIST_SINK_ID') || '');
  const [selectedMicId, setSelectedMicId] = useState(() => localStorage.getItem('CATINTASSIST_MIC_ID') || '');
  // v4.103.0: separate mic for recording soundboard greetings ('' = use the call mic).
  const [selectedRecMicId, setSelectedRecMicId] = useState(() => localStorage.getItem('CATINTASSIST_REC_MIC_ID') || '');
  
  const [localVolume, setLocalVolume] = useState(() => parseFloat(localStorage.getItem('CATINTASSIST_LOCAL_VOL') || '1'));
  const [sinkVolume, setSinkVolume] = useState(() => parseFloat(localStorage.getItem('CATINTASSIST_SINK_VOL') || '1'));
  const [monitorMic, setMonitorMic] = useState(false);
  const [monitorVolume, setMonitorVolume] = useState(0.5);
  const monitorCtxRef = useRef(null);
  const monitorGainRef = useRef(null);
  const monitorStreamRef = useRef(null);

  const changeLocalVolume = (vol) => { setLocalVolume(vol); localStorage.setItem('CATINTASSIST_LOCAL_VOL', vol); };
  const changeSinkVolume = (vol) => { setSinkVolume(vol); localStorage.setItem('CATINTASSIST_SINK_VOL', vol); };
  
  // Hidden element: physical mic → virtual output (when mic is selected)
  const passthroughAudioRef = useRef(new Audio());
  const micStreamRef = useRef(null);
  const clipPlaybackStopRef = useRef(null);
  const clipGenerationRef = useRef(0);
  const currentSinkRef = useRef(selectedSinkId);
  currentSinkRef.current = selectedSinkId;
  const sinkPlaybackActiveRef = useRef(false);
  const [sinkPlaybackActive, setSinkPlaybackActiveState] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [micStatus, setMicStatus] = useState('idle'); // idle | ok | no-signal | clip | muted
  const micThrottleRef = useRef(0);
  const micSilentSinceRef = useRef(0);

  /** Mute mic passthrough while soundboard/TTS plays to the same sink (avoids garbled mix). */
  const setSinkPlaybackActive = useCallback((active) => {
    sinkPlaybackActiveRef.current = active;
    setSinkPlaybackActiveState(active);
    const el = passthroughAudioRef.current;
    if (!el) return;
    el.volume = active ? 0 : 1;
    el.muted = active;
  }, []);

  // Fetch available devices
  const fetchDevices = useCallback(async ({ requestMicPermissionForLabels = false } = {}) => {
    try {
      let devices = await navigator.mediaDevices.enumerateDevices();
      const needsPermission = devices.some(d => d.label === '' || d.label.toLowerCase() === 'speaker' || d.label.toLowerCase() === 'microphone');
      if (needsPermission && requestMicPermissionForLabels) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());
          devices = await navigator.mediaDevices.enumerateDevices();
        } catch (e) {
          catWarn('[Audio:perm] Microphone permission denied.', e);
        }
      }
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      const inputs = devices.filter(d => d.kind === 'audioinput');
      setOutputDevices(outputs);
      setInputDevices(inputs);
      // v4.87.0: remember every real label — picker stays readable on fresh origins.
      try { rememberDeviceLabels([...outputs, ...inputs]); } catch (_) {}
      let nextSinkId = selectedSinkId;
      if (nextSinkId && !outputs.some((d) => d.deviceId === nextSinkId)) {
        nextSinkId = '';
        currentSinkRef.current = '';
        setSelectedSinkId('');
        persistSinkExplicit(false); // device gone — explicit pick no longer applies
      }
      // ponytail: cable mode — auto-fix VB out when empty or stuck on speakers/default.
      // Never touches an explicit user pick (selection-stick fix).
      if (readAudioSourceMode() === AUDIO_SOURCE_MODE_VIRTUAL_CABLE) {
        const pickedSink = pickVbCableSinkDevice(outputs);
        const sinkLabel = outputs.find((d) => d.deviceId === nextSinkId)?.label || '';
        if (
          pickedSink &&
          shouldAutoFixSink({ explicit: readSinkExplicit(), sinkId: nextSinkId, sinkLabel })
        ) {
          nextSinkId = pickedSink;
          currentSinkRef.current = pickedSink;
          setSelectedSinkId(pickedSink);
          try {
            localStorage.setItem('CATINTASSIST_SINK_ID', pickedSink);
          } catch (_) {}
        }
      }
    } catch (err) {
      catError('[Audio:device] Failed to enumerate audio devices:', err);
    }
  }, [selectedSinkId]);

  useEffect(() => {
    fetchDevices({ requestMicPermissionForLabels: false });
    const onDeviceChange = () => {
      // Avoid triggering mic permission during casual device changes.
      fetchDevices({ requestMicPermissionForLabels: false });
    };
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      try {
        navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
      } catch (_) {}
    };
  }, [fetchDevices]);

  // Core Mixer Logic: If both Mic and Sink are selected, pipe them together.
  useEffect(() => {
    let stream = null;
    let isMounted = true;

    const setupPassthrough = async () => {
      // 1. Point the pipeline's output to the Virtual Microphone
      if (passthroughAudioRef.current.setSinkId) {
        try { await passthroughAudioRef.current.setSinkId(selectedSinkId || ''); } 
        catch (e) { catError('[Audio:sink] setSinkId fail on passthrough:', e); }
      }

      // 2. Feed the actual Physical Mic into the pipeline
      if (selectedMicId && isMounted) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ 
             audio: { deviceId: { exact: selectedMicId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false } 
          });
          if (isMounted) {
            // v4.185.0: the mic stream feeds the level METER only. It is never
            // played to the sink — the old live forward sent whatever this
            // device carried (CABLE Output = music + the caller) to the client.
            // Your voice reaches the call through Voicemeeter (Strip 0 → B1).
            micStreamRef.current = stream;

            // Attach visualizer without React state to avoid massive re-renders
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioCtx.createMediaStreamSource(stream);
            const analyzer = audioCtx.createAnalyser();
            analyzer.fftSize = 256;
            source.connect(analyzer);
            
            const dataArray = new Uint8Array(analyzer.frequencyBinCount);

            // v4.93.1 CPU fix: this loop runs forever at 60fps while a mic is
            // selected. Analyze ~every 6th frame (≈10Hz — plenty for a meter)
            // and skip React state + DOM writes when the rounded value is
            // unchanged, so idle silence costs ~zero.
            let meterFrame = 0;
            let lastMeterVol = -1;
            let lastBarW = -1;
            const updateVolume = () => {
              if (!isMounted) {
                audioCtx.close().catch((e) => catWarn('[Audio:ctx] audioCtx close fail:', e));
                return;
              }
              meterFrame += 1;
              if (meterFrame % 6 !== 0) {
                requestAnimationFrame(updateVolume);
                return;
              }
              analyzer.getByteFrequencyData(dataArray);
              let sum = 0;
              for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
              const avg = sum / dataArray.length;

              // Scale volume. Average is rarely > 60 in normal talking.
              const micVol = Math.min(100, (avg / 60) * 100);
              const appVol = window.__CAT_AUDIO_VOL || 0;
              const vol = Math.round(Math.min(100, micVol + appVol));
              const changed = vol !== lastMeterVol;
              lastMeterVol = vol;

              // Throttled React state + global ref for legacy top-mic-bar.
              // React bails out on identical setState values, so the only
              // real cost is running this block — skip it when the rounded
              // level didn't move during active speech. Quiet frames still
              // run so the 3s no-signal timer can fire.
              const now = Date.now();
              const quiet = vol <= 2 && !sinkPlaybackActiveRef.current;
              if ((changed || quiet) && now - micThrottleRef.current > 100) {
                micThrottleRef.current = now;
                window.__CAT_MIC_LEVEL = vol;
                if (changed) setMicLevel(vol);
                if (sinkPlaybackActiveRef.current) {
                  setMicStatus('muted');
                } else if (vol > 90) {
                  setMicStatus('clip');
                } else if (vol > 2) {
                  micSilentSinceRef.current = 0;
                  setMicStatus('ok');
                } else {
                  if (!micSilentSinceRef.current) micSilentSinceRef.current = now;
                  setMicStatus(now - micSilentSinceRef.current > 3000 ? 'no-signal' : 'ok');
                }
              }

              const bar = document.getElementById('top-mic-bar');
              if (bar) {
                const w = vol > 2 ? vol : 0;
                if (w !== lastBarW) {
                  lastBarW = w;
                  if (vol > 2) {
                    bar.style.width = `${vol}%`;
                    bar.style.opacity = Math.min(1, (vol / 40) + 0.2).toString();
                  // Change color based on clipping
                  if (vol > 90) {
                     bar.style.background = '#ef4444';
                     bar.style.boxShadow = '0 0 10px #ef4444';
                  } else if (vol > 70) {
                     bar.style.background = '#f59e0b';
                     bar.style.boxShadow = '0 0 10px #f59e0b';
                  } else {
                     bar.style.background = '#10b981';
                     bar.style.boxShadow = '0 0 10px #10b981';
                  }
                } else {
                  bar.style.width = '0%';
                  bar.style.opacity = '0';
                }
                } // end if (w !== lastBarW)
              }
              requestAnimationFrame(updateVolume);
            };
            updateVolume();
          }
        } catch (e) {
          catError('[Audio:mic] Failed to capture physical mic for passthrough:', e);
          setMicStatus('no-signal');
          setMicLevel(0);
        }
      } else {
        micStreamRef.current = null;
        if (!clipPlaybackStopRef.current) {
          passthroughAudioRef.current.srcObject = null;
        }
        setMicLevel(0);
        setMicStatus('idle');
      }
    };
    
    setupPassthrough();

    const currentPipeline = passthroughAudioRef.current;
    return () => {
      isMounted = false;
      if (stream) stream.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
      if (!clipPlaybackStopRef.current) {
        currentPipeline.srcObject = null;
      }
    };
  }, [selectedMicId, selectedSinkId]);

  /** v4.185.0: the sink carries greetings ONLY — empty it when no clip plays. */
  const silenceSinkElement = () => {
    const el = passthroughAudioRef.current;
    if (!el) return;
    try {
      el.pause?.();
      el.srcObject = null;
    } catch (_) {}
  };

  /**
   * Panic stop (v4.86.2; v4.185.0): kill any clip, leave the sink silent.
   * Name kept so the "Restore Mic" buttons still work — your mic does not
   * go through the app any more, so "restore" = stop the clip.
   */
  const restoreLiveMic = useCallback((reason = 'manual') => {
    clipGenerationRef.current += 1;
    try { clipPlaybackStopRef.current?.(); } catch (_) {}
    clipPlaybackStopRef.current = null;
    sinkPlaybackActiveRef.current = false;
    setSinkPlaybackActiveState(false);
    silenceSinkElement();
    logRouteEvent(ROUTE_EVENT.PASSTHROUGH_RESTORE, { routeMode: 'panic', reason });
    return { ok: true };
  }, []);

  /** Stop clip playback; the sink goes silent (never back to a live mic). */
  const stopClipToSink = useCallback(() => {
    clipGenerationRef.current += 1;
    if (clipPlaybackStopRef.current) {
      clipPlaybackStopRef.current();
      clipPlaybackStopRef.current = null;
    }
    sinkPlaybackActiveRef.current = false;
    setSinkPlaybackActiveState(false);
    silenceSinkElement();
    logRouteEvent(ROUTE_EVENT.PASSTHROUGH_RESTORE, { routeMode: ROUTE_MODE.PASSTHROUGH });
  }, []);

  useEffect(() => stopClipToSink, [selectedSinkId, stopClipToSink]);

  /**
   * Route clip through passthrough element (same VB-Cable path as live mic).
   * @returns {Promise<{ ok: boolean, mode?: string, reason?: string }>}
   */
  const playClipToSink = useCallback(async (blob, volume = 1, { clipKey, onProgress, onLevel } = {}) => {
    if (!blob || !selectedSinkId) {
      return { ok: false, reason: 'no_sink_or_blob' };
    }
    stopClipToSink();
    const generation = clipGenerationRef.current;
    const entrySink = selectedSinkId;
    // Cancellation is terminal: neither fallback engine may revive an old fire.
    const cancellation = () => {
      if (currentSinkRef.current !== entrySink) return 'sink_changed';
      if (clipGenerationRef.current !== generation) return 'play_cancelled';
      return null;
    };
    const cancelledResult = () => ({ ok: false, cancelled: true, reason: cancellation() });

    const el = passthroughAudioRef.current;

    // v4.104.0: direct ctx.setSinkId render first — buffered output on the sink's
    // own clock (no live MediaStream, no srcObject swap). The "can of tuna" chop
    // came from the old element-swap path below, which stays as fallback.
    // v4.108.1: resume + setSinkId on EVERY fire — without this the persistent
    // ctx renders to the default output (speakers), never Voicemeeter Input.
    try {
      const ctx = getDirectSinkContext(entrySink);
      await ctx.resume();
      if (cancellation()) return cancelledResult();
      await ctx.setSinkId(entrySink);
      if (cancellation()) return cancelledResult();
      const buffer = await decodeBlobOnContext(ctx, blob);
      if (cancellation()) return cancelledResult();
      sinkPlaybackActiveRef.current = true;
      setSinkPlaybackActiveState(true);
      setSinkPlaybackActive(true); // duck live mic during clip (same as before, minus swap)
      const session = playBufferDirect(ctx, buffer, { volume, onProgress, onLevel });
      clipPlaybackStopRef.current = session.stop;

      logRouteEvent(ROUTE_EVENT.DIRECT_INJECT, { clipKey, sinkId: selectedSinkId });

      await session.promise;
      if (cancellation()) return cancelledResult();

      clipPlaybackStopRef.current = null;
      sinkPlaybackActiveRef.current = false;
      setSinkPlaybackActive(false);
      logRouteEvent(ROUTE_EVENT.PLAY_END, { clipKey, routeMode: ROUTE_MODE.DIRECT_SINK });
      return { ok: true, mode: ROUTE_MODE.DIRECT_SINK };
    } catch (directErr) {
      if (cancellation()) return cancelledResult();
      // Unsupported browser, autoplay/resume rejected, or bad sinkId → fall back.
      if (directErr?.message !== 'direct_sink_unsupported') {
        logRouteEvent(ROUTE_EVENT.PLAY_FAIL, {
          clipKey,
          routeMode: ROUTE_MODE.DIRECT_SINK,
          reason: directErr?.message || 'direct_failed',
        });
      }
      clipPlaybackStopRef.current = null;
      sinkPlaybackActiveRef.current = false;
      setSinkPlaybackActive(false);
    }

    try {
      const { ctx, buffer } = await decodeBlobToBuffer(blob);
      if (cancellation()) {
        ctx.close().catch(() => {});
        return cancelledResult();
      }
      sinkPlaybackActiveRef.current = true;
      setSinkPlaybackActiveState(true);

      const session = playBufferViaPassthrough(el, buffer, ctx, {
        volume,
        sinkId: selectedSinkId,
        savedSrcObject: null, // v4.185.0: after the clip the sink is silent
        onProgress,
      });
      clipPlaybackStopRef.current = session.stop;

      logRouteEvent(ROUTE_EVENT.PASSTHROUGH_INJECT, {
        clipKey,
        routeMode: ROUTE_MODE.PASSTHROUGH,
        sinkId: selectedSinkId,
      });

      await session.promise;
      if (cancellation()) return cancelledResult();

      clipPlaybackStopRef.current = null;
      sinkPlaybackActiveRef.current = false;
      setSinkPlaybackActiveState(false);
      silenceSinkElement();
      logRouteEvent(ROUTE_EVENT.PLAY_END, { clipKey, routeMode: ROUTE_MODE.PASSTHROUGH });
      return { ok: true, mode: ROUTE_MODE.PASSTHROUGH };
    } catch (err) {
      if (cancellation()) return cancelledResult();
      catError('[Audio:sink] playClipToSink failed:', err);
      clipPlaybackStopRef.current = null;
      sinkPlaybackActiveRef.current = false;
      setSinkPlaybackActiveState(false);
      silenceSinkElement();
      logRouteEvent(ROUTE_EVENT.PLAY_FAIL, {
        clipKey,
        routeMode: ROUTE_MODE.PASSTHROUGH,
        reason: err?.message || 'unknown',
      });
      return { ok: false, reason: err?.message || 'play_failed', mode: ROUTE_MODE.PASSTHROUGH };
    }
  }, [selectedSinkId, stopClipToSink, setSinkPlaybackActive]);

  // Mic Monitor: hear your own voice through local speakers
  useEffect(() => {
    if (monitorMic) {
      const constraints = selectedMicId
        ? { audio: { deviceId: { exact: selectedMicId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false } }
        : { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } };
      navigator.mediaDevices.getUserMedia(constraints).then(stream => {
        monitorStreamRef.current = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        monitorCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const gain = ctx.createGain();
        gain.gain.value = monitorVolume;
        monitorGainRef.current = gain;
        source.connect(gain);
        gain.connect(ctx.destination); // local speakers only
      }).catch(e => { catError('[Audio:mic] Mic monitor failed:', e); setMonitorMic(false); });
    } else {
      if (monitorStreamRef.current) { monitorStreamRef.current.getTracks().forEach(t => t.stop()); monitorStreamRef.current = null; }
      if (monitorCtxRef.current) { monitorCtxRef.current.close().catch(()=>{}); monitorCtxRef.current = null; }
    }
    return () => {
      if (monitorStreamRef.current) { monitorStreamRef.current.getTracks().forEach(t => t.stop()); monitorStreamRef.current = null; }
      if (monitorCtxRef.current) { monitorCtxRef.current.close().catch(()=>{}); monitorCtxRef.current = null; }
    };
  }, [monitorMic, selectedMicId, monitorVolume]);

  useEffect(() => {
    if (monitorGainRef.current) monitorGainRef.current.gain.value = monitorVolume;
  }, [monitorVolume]);

  const changeSinkId = (deviceId) => {
    currentSinkRef.current = deviceId;
    stopClipToSink();
    setSelectedSinkId(deviceId);
    localStorage.setItem('CATINTASSIST_SINK_ID', deviceId);
    persistSinkExplicit(!!deviceId); // hand-picked — auto-fix keeps hands off
  };

  const changeMicId = (deviceId) => {
    setSelectedMicId(deviceId);
    localStorage.setItem('CATINTASSIST_MIC_ID', deviceId);
    try {
      window.dispatchEvent(
        new CustomEvent('catint_mic_device_changed', { detail: { deviceId } }),
      );
    } catch (_) {}
  };

  const changeRecMicId = (deviceId) => {
    setSelectedRecMicId(deviceId);
    try { localStorage.setItem('CATINTASSIST_REC_MIC_ID', deviceId); } catch (_) {}
  };

  return (
    <AudioSettingsContext.Provider value={{
      outputDevices,
      inputDevices,
      selectedSinkId,
      selectedMicId,
      selectedRecMicId,
      changeSinkId,
      changeMicId,
      changeRecMicId,
      fetchDevices,
      localVolume,
      sinkVolume,
      changeLocalVolume,
      changeSinkVolume,
      monitorMic,
      setMonitorMic,
      monitorVolume,
      setMonitorVolume,
      setSinkPlaybackActive,
      sinkPlaybackActive,
      micLevel,
      micStatus,
      playClipToSink,
      stopClipToSink,
      restoreLiveMic,
      routeModePreference: readRouteModePreference(),
    }}>
      {children}
    </AudioSettingsContext.Provider>
  );
};

export const useAudioSettings = () => useContext(AudioSettingsContext);
